<template>
  <div class="py-4 px-2 p-md-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Analytics Alert</h5>
      <a-button type="primary" @click="openAddEditor">
        <template #icon><i class="bi bi-plus-circle mr-1"></i></template>
        Add Analytics Alert
      </a-button>
    </div>

    <p class="text-muted">
      Schedule the Operation Level Analytics report as a PDF and have it emailed
      to one or more recipients.
    </p>

    <a-table
      :data-source="configs"
      :columns="columns"
      :loading="isFetching"
      row-key="id"
      :pagination="{ pageSize: 20 }"
      :scroll="{ x: 'max-content' }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'task'">
          {{ getTaskLabel(record.task) }}
        </template>
        <template v-if="column.key === 'schedule'">
          {{ describeSchedule(record) }}
        </template>
        <template v-if="column.key === 'is_active'">
          <a-tag :color="record.is_active ? 'blue' : 'default'">
            {{ record.is_active ? 'Active' : 'Inactive' }}
          </a-tag>
        </template>
        <template v-if="column.key === 'recipients'">
          <span
            v-if="!record.recipients || record.recipients.length === 0"
            class="text-muted"
          >
            None
          </span>
          <span v-else>
            <a-tag
              v-for="r in record.recipients"
              :key="r.id"
              style="margin-bottom: 2px"
            >
              {{ r.email }}
            </a-tag>
          </span>
        </template>
        <template v-if="column.key === 'last_sent_at'">
          <span v-if="!record.last_sent_at" class="text-muted">Never</span>
          <span v-else>{{ formatSentAt(record.last_sent_at) }}</span>
        </template>
        <template v-if="column.key === 'actions'">
          <a-space>
            <a-button size="small" @click="openEditEditor(record)">
              Edit
            </a-button>
            <a-popconfirm
              :title="`Send this report to ${
                record.recipients ? record.recipients.length : 0
              } recipient(s) now?`"
              ok-text="Send"
              cancel-text="Cancel"
              @confirm="handleSendNow(record)"
            >
              <a-button
                size="small"
                type="primary"
                ghost
                :disabled="!record.recipients || !record.recipients.length"
                :loading="sendingId === record.id"
              >
                Send Now
              </a-button>
            </a-popconfirm>
            <a-button
              size="small"
              :loading="previewingId === record.id"
              @click="handleDownloadPreview(record)"
            >
              Download PDF
            </a-button>
            <a-popconfirm
              title="Delete this analytics alert?"
              ok-text="Yes"
              cancel-text="No"
              @confirm="handleDelete(record.id)"
            >
              <a-button size="small" danger>Delete</a-button>
            </a-popconfirm>
          </a-space>
        </template>
      </template>
    </a-table>

    <AlertEditor
      :visible="showEditor"
      :config="editingConfig"
      :is-submitting="isSubmitting"
      @close="closeEditor"
      @save="handleSave"
      @send-now="handleSendNow"
      @add-recipient="handleAddRecipient"
      @delete-recipient="handleDeleteRecipient"
    />
  </div>
</template>

<script>
import downloadBlob from 'src/components/shared/Helpers/downloadBlob';
import AnalyticsAlertService from 'src/services/analyticsAlert';
import dayjs from 'dayjs';
import { mapActions, mapGetters } from 'vuex';
import AlertEditor from './AlertEditor.vue';
import { describeSchedule } from './schedule';

export default {
  name: 'AnalyticsAlerts',
  components: { AlertEditor },
  inject: ['toast'],

  data() {
    return {
      configs: [],
      isFetching: false,
      isSubmitting: false,
      showEditor: false,
      editingConfig: null,
      sendingId: null,
      previewingId: null,
      columns: [
        { title: 'Name', key: 'name', dataIndex: 'name' },
        { title: 'Operation', key: 'task' },
        { title: 'Schedule', key: 'schedule' },
        { title: 'Recipients', key: 'recipients' },
        { title: 'Last Sent', key: 'last_sent_at' },
        { title: 'Status', key: 'is_active' },
        { title: 'Actions', key: 'actions' },
      ],
    };
  },

  computed: {
    ...mapGetters(['organization', 'allTasks', 'devices']),
  },

  created() {
    if (!this.allTasks || this.allTasks.length === 0) this.getAllTasks();
    if (!this.devices || this.devices.length === 0) {
      this.getOrgDevices(this.organization);
    }
    this.fetchConfigs();
  },

  methods: {
    ...mapActions(['getAllTasks', 'getOrgDevices']),

    describeSchedule,

    async fetchConfigs() {
      this.isFetching = true;
      const [error, data] = await AnalyticsAlertService.fetchAnalyticsAlerts(
        this.organization
      );
      this.isFetching = false;
      if (error) {
        this.toast.error('Failed to load analytics alerts');
        return;
      }
      this.configs = data || [];
    },

    getTaskLabel(taskId) {
      const task = (this.allTasks || []).find((t) => t.id === taskId);
      return task ? task.taskName : `Operation #${taskId}`;
    },

    formatSentAt(value) {
      return dayjs(value).format('YYYY-MM-DD HH:mm');
    },

    openAddEditor() {
      this.editingConfig = null;
      this.showEditor = true;
    },

    openEditEditor(config) {
      this.editingConfig = config;
      this.showEditor = true;
    },

    closeEditor() {
      this.showEditor = false;
      this.editingConfig = null;
    },

    async handleSave(body, pendingRecipients) {
      this.isSubmitting = true;

      if (this.editingConfig) {
        const [error, data] = await AnalyticsAlertService.updateAnalyticsAlert(
          this.editingConfig.id,
          body
        );
        this.isSubmitting = false;
        if (error) {
          this.toast.error('Failed to update analytics alert');
          return;
        }
        const idx = this.configs.findIndex(
          (c) => c.id === this.editingConfig.id
        );
        if (idx !== -1) {
          this.configs[idx] = { ...this.configs[idx], ...data };
          this.configs = [...this.configs];
        }
        this.toast.success('Analytics alert updated');
      } else {
        const [error, data] = await AnalyticsAlertService.createAnalyticsAlert(
          body
        );
        if (error) {
          this.isSubmitting = false;
          this.toast.error('Failed to create analytics alert');
          return;
        }

        if (pendingRecipients && pendingRecipients.length > 0) {
          const results = await Promise.all(
            pendingRecipients.map((email) =>
              AnalyticsAlertService.addRecipient(data.id, email)
            )
          );
          if (results.some(([recipientError]) => recipientError)) {
            this.toast.error('Some recipients could not be added');
          }
          await this.fetchConfigs();
        } else {
          this.configs = [...this.configs, { ...data, recipients: [] }];
        }
        this.isSubmitting = false;
        this.toast.success('Analytics alert created');
      }

      this.closeEditor();
    },

    async handleDelete(id) {
      const [error] = await AnalyticsAlertService.deleteAnalyticsAlert(id);
      if (error) {
        this.toast.error('Failed to delete analytics alert');
        return;
      }
      this.configs = this.configs.filter((c) => c.id !== id);
      this.toast.success('Analytics alert deleted');
    },

    async handleSendNow(config) {
      this.sendingId = config.id;
      const [error] = await AnalyticsAlertService.sendAnalyticsAlertNow(
        config.id
      );
      this.sendingId = null;
      if (error) {
        this.toast.error('Failed to send the report');
        return;
      }
      const count = config.recipients ? config.recipients.length : 0;
      this.toast.success(`Report queued for ${count} recipient(s)`);
    },

    async handleDownloadPreview(config) {
      this.previewingId = config.id;
      const [error, file] =
        await AnalyticsAlertService.downloadAnalyticsAlertPreview(config.id);
      this.previewingId = null;
      if (error) {
        this.toast.error('Failed to generate the report PDF');
        return;
      }
      downloadBlob(
        file.blob,
        file.filename || `analytics_report_${config.id}.pdf`
      );
    },

    async handleAddRecipient(configId, email) {
      const [error, data] = await AnalyticsAlertService.addRecipient(
        configId,
        email
      );
      if (error) {
        this.toast.error('Failed to add recipient');
        return;
      }
      // editingConfig IS the same object reference as configs[n], so only update once
      const config = this.configs.find((c) => c.id === configId);
      if (config) config.recipients = [...(config.recipients || []), data];
    },

    async handleDeleteRecipient(configId, recipientId) {
      const [error] = await AnalyticsAlertService.deleteRecipient(recipientId);
      if (error) {
        this.toast.error('Failed to remove recipient');
        return;
      }
      // editingConfig IS the same object reference as configs[n], so only update once
      const config = this.configs.find((c) => c.id === configId);
      if (config) {
        config.recipients = config.recipients.filter(
          (r) => r.id !== recipientId
        );
      }
    },
  },
};
</script>
